import type { FeatureFlagService } from '@affine/core/modules/feature-flag';
import { opfsBlobCache } from '@affine/core/utils/opfs-blob-cache';
import { Entity, LiveData, yjsGetPath } from '@toeverything/infra';
import type { Observable } from 'rxjs';
import { Doc as YDoc, transact } from 'yjs';

import { DocsService } from '../../doc';
import { WorkspaceImpl } from '../impls/workspace';
import type { WorkspaceScope } from '../scopes/workspace';
import { WorkspaceEngineService } from '../services/engine';
import {
  openWorkspaceBlobRange,
  resolveWorkspaceBlobFromCacheOrRemote,
  resolveWorkspaceBlobMetadata,
  resolveWorkspaceBlobStreamUrl,
  workspaceBlobCacheToken,
  type WorkspaceBlobContext,
  WorkspaceBlobLocalStorageUnavailableError,
  workspaceBlobNamespace,
} from '../utils/blob-cache';
import { isDatabaseSystemCollectionDocId } from '../utils/database-row-shard-docs';

export class Workspace extends Entity {
  constructor(
    public readonly scope: WorkspaceScope,
    public readonly featureFlagService: FeatureFlagService
  ) {
    super();
  }

  readonly id = this.scope.props.openOptions.metadata.id;

  readonly openOptions = this.scope.props.openOptions;

  readonly meta = this.scope.props.openOptions.metadata;

  readonly flavour = this.meta.flavour;

  readonly rootYDoc = new YDoc({ guid: this.openOptions.metadata.id });

  _docCollection: WorkspaceImpl | null = null;

  private disposed = false;

  readonly blobContext: WorkspaceBlobContext = {
    local: (() => {
      const opts = this.scope.props.engineWorkerInitOptions.local.blob?.opts;
      if (
        !opts ||
        !('flavour' in opts) ||
        !('type' in opts) ||
        !('id' in opts) ||
        typeof opts.flavour !== 'string' ||
        (opts.type !== 'workspace' && opts.type !== 'userspace') ||
        typeof opts.id !== 'string'
      ) {
        throw new Error('Initialized local blob storage required');
      }
      return { flavour: opts.flavour, type: opts.type, id: opts.id };
    })(),
    external: Boolean(this.openOptions.externalDocAccess),
    assertActive: () => {
      const access = this.openOptions.externalDocAccess;
      if (
        this.disposed ||
        (access && Date.parse(access.expiresAt) <= Date.now())
      ) {
        throw new DOMException('Workspace access ended', 'AbortError');
      }
    },
    canReadPending: () => {
      const access = this.openOptions.externalDocAccess;
      return (
        !access || (access.role === 'Editor' && Boolean(access.upload?.enabled))
      );
    },
  };

  /** Range reads of one blob under this workspace's blob access rules. */
  openBlobRange(key: string) {
    return openWorkspaceBlobRange({
      workspaceId: this.openOptions.metadata.id,
      key,
      context: this.blobContext,
    });
  }

  get docCollection() {
    if (!this._docCollection) {
      const workspaceId = this.openOptions.metadata.id;
      const isRestrictedCloudMetadataDoc = (docId: string) =>
        this.flavour === 'affine-cloud' &&
        (docId === workspaceId ||
          docId === `db$${workspaceId}$folders` ||
          docId === `db$${workspaceId}$docProperties`);

      this._docCollection = new WorkspaceImpl({
        id: workspaceId,
        externalDocAccess: Boolean(this.openOptions.externalDocAccess),
        rootDoc: this.rootYDoc,
        featureFlagService: this.featureFlagService,
        blobSource: {
          get: async key => {
            const workspaceId = this.openOptions.metadata.id;
            return await resolveWorkspaceBlobFromCacheOrRemote({
              workspaceId,
              key,
              context: this.blobContext,
            });
          },
          getMetadata: async key => {
            return resolveWorkspaceBlobMetadata({
              workspaceId,
              key,
              context: this.blobContext,
            });
          },
          streamUrl: async key => {
            return resolveWorkspaceBlobStreamUrl({
              workspaceId,
              key,
              context: this.blobContext,
            });
          },
          delete: async () => {
            return;
          },
          list: async () => {
            return [];
          },
          set: async (id, blob) => {
            this.blobContext.assertActive();
            const cached = await opfsBlobCache.set(
              workspaceBlobNamespace(this.blobContext.local),
              id,
              blob,
              {
                mime: blob.type,
                size: blob.size,
                source: 'upload',
                cacheToken: workspaceBlobCacheToken(id, blob.size),
              }
            );
            if (!cached) {
              throw new WorkspaceBlobLocalStorageUnavailableError();
            }
            const record = {
              key: id,
              source: blob,
              mime: blob.type,
              size: blob.size,
            };
            this.blobContext.assertActive();
            await this.engine.blob.set(record);
            // Image/doc CRDT updates may reference this blob immediately after
            // set() resolves. Wait for authoritative cloud upload so other
            // clients do not render permanent "Image not found" placeholders.
            await this.engine.blob.upload(record);
            return id;
          },
          /* eslint-disable rxjs/finnish */
          blobState$: key => this.engine.blob.blobState$(key),
          upload: async key => {
            this.blobContext.assertActive();
            const cached = await opfsBlobCache.getCompleteFile(
              workspaceBlobNamespace(this.blobContext.local),
              key
            );
            this.blobContext.assertActive();
            if (!cached) {
              return this.engine.blob.upload(key);
            }
            return this.engine.blob.upload({
              key,
              source: cached,
              mime: cached.type || 'application/octet-stream',
              size: cached.size,
            });
          },
          name: 'blob',
          readonly: false,
        },
        onLoadDoc: doc => {
          if (isRestrictedCloudMetadataDoc(doc.guid)) {
            return;
          }
          this.engine.doc.connectDoc(doc);
          // Database roots/shards carry explicit page/window/edit owner references.
          // A permanent generic load reason would defeat their window release.
          if (!isDatabaseSystemCollectionDocId(doc.guid, this.id)) {
            this.engine.doc.addDoc(doc.guid, 'workspace-doc-load');
          }
        },
        onLoadAwareness: awareness =>
          this.engine.awareness.connectAwareness(awareness),
        shouldLoadAwareness: docId =>
          !isRestrictedCloudMetadataDoc(docId) &&
          !isDatabaseSystemCollectionDocId(docId, this.id),
        onCreateDoc: docId =>
          this.docs.createDoc({ id: docId, skipInit: true }).id,
      });

      // SAFETY: Do NOT auto-delete docs from storage on docMetaRemoved.
      //
      // docMetaRemoved fires whenever _handleDocMetaEvent() detects that a doc ID
      // is no longer in meta.pages. This happens during normal Y.js CRDT sync —
      // e.g. when a remote update temporarily makes pages unreadable or when
      // this._proxy.pages returns undefined (→ docMetas = []) during a merge.
      //
      // Calling deleteDoc() here irreversibly destroys doc data in IDB AND the
      // backend. A single false-positive docMetaRemoved event during sync deletes
      // all user documents permanently. This has caused repeated data loss.
      //
      // Doc storage cleanup must happen through explicit user actions only
      // (e.g. permanent delete from trash), never through CRDT sync observers.
      this._docCollection.meta.docMetaRemoved.subscribe(docId => {
        console.warn(
          `[Workspace] docMetaRemoved fired for ${docId} — storage NOT deleted (safety guard)`
        );
      });
    }
    return this._docCollection;
  }

  get docs() {
    return this.scope.get(DocsService);
  }

  get canGracefulStop() {
    // TODO
    return true;
  }

  get engine() {
    return this.framework.get(WorkspaceEngineService).engine;
  }

  name$ = LiveData.from<string | undefined>(
    yjsGetPath(this.rootYDoc.getMap('meta'), 'name') as Observable<
      string | undefined
    >,
    undefined
  );

  avatar$ = LiveData.from(
    yjsGetPath(this.rootYDoc.getMap('meta'), 'avatar') as Observable<
      string | undefined
    >,
    undefined
  );

  setAvatar(avatar: string) {
    transact(
      this.rootYDoc,
      () => {
        this.rootYDoc.getMap('meta').set('avatar', avatar);
      },
      { force: true }
    );
  }

  setName(name: string) {
    transact(
      this.rootYDoc,
      () => {
        this.rootYDoc.getMap('meta').set('name', name);
      },
      { force: true }
    );
  }

  override dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    this._docCollection?.dispose();
    super.dispose();
  }
}
